import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getRetentionReport } from "../../../actions/adminReportActions";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

import Loader from "../../Loader";
import { niceMax } from '../../../utils/chartPlugins';
import { downloadCSV } from '../../../utils/exportCsv';

const RetentionReport = () => {
  const dispatch = useDispatch();
  const { retentionReport, loading } = useSelector(state => state.adminReport);

  const [dateRange, setDateRange] = useState('last30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const rangeLabel = (dr, s, e) => {
    if (dr === 'custom') return s && e ? `${s} → ${e}` : 'Custom range';
    if (dr === 'last7days') return 'Last 7 Days';
    if (dr === 'last30days') return 'Last 30 Days';
    if (dr === 'last90days') return 'Last 90 Days';
    if (dr === 'ytd') return 'Year to Date';
    return dr;
  };

  useEffect(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        dispatch(getRetentionReport('custom', customStartDate, customEndDate));
      }
    } else {
      dispatch(getRetentionReport(dateRange));
    }
  }, [dispatch, dateRange, customStartDate, customEndDate]);

  const usersInRange = retentionReport?.usersInRange || 0;
  const retainedUsers = retentionReport?.retainedUsers || 0;

  const chartData = {
    labels: ['Retention'],
    datasets: [
      { label: 'Users In Range', data: [Number(usersInRange)], backgroundColor: '#00a65a' },
      { label: 'Retained Users', data: [Number(retainedUsers)], backgroundColor: '#3c8dbc' }
    ]
  };

  const chartOptions = { responsive: true, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true } } };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Retention Report
            <small>User retention and cohort analysis</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Reports</li>
            <li className="active">Retention</li>
          </ol>
        </section>

        <section className="content">
          <div className="box box-info">
            <div className="box-header with-border">
              <h3 className="box-title">Filter Report</h3>
            </div>
            <div className="box-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Date Range</label>
                    <select className="form-control" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                      <option value="last7days">Last 7 Days</option>
                      <option value="last30days">Last 30 Days</option>
                      <option value="last90days">Last 90 Days</option>
                      <option value="ytd">Year to Date</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                </div>
                {dateRange === 'custom' && (
                  <>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Start Date</label>
                        <input type="date" className="form-control" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>End Date</label>
                        <input type="date" className="form-control" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                      </div>
                    </div>
                  </>
                )}
                <div className="col-md-6" style={{display:'flex', alignItems:'center', justifyContent:'flex-end'}}>
                  <div style={{marginRight:8, alignSelf:'center'}}>Showing: <strong>{rangeLabel(dateRange, customStartDate, customEndDate)}</strong></div>
                  <button className="btn btn-default btn-sm" onClick={() => downloadCSV('retention_report.csv', [{ usersInRange: usersInRange, retainedUsers: retainedUsers, retentionRate: retentionReport?.retentionRate || 0 }])}>Export CSV</button>
                </div>
              </div>
            </div>
          </div>
          <div className="box box-success">
            <div className="box-header with-border">
              <h3 className="box-title">Retention Overview</h3>
            </div>
            <div className="box-body">
              {loading ? <Loader /> : (
                <>
                  <p>Users in Range: {retentionReport?.usersInRange || 0}</p>
                  <p>Retained Users: {retentionReport?.retainedUsers || 0}</p>
                  <p>Retention Rate: {Math.round((retentionReport?.retentionRate || 0) * 100 * 100) / 100}%</p>
                  <div style={{height:320}}>
                    {(chartData.datasets[0].data.every(v => v === 0) && chartData.datasets[1].data.every(v => v === 0)) ? (
                      <p className="text-center">No retention time-series data for the selected range.</p>
                    ) : (
                      <Bar data={chartData} options={{...chartOptions, maintainAspectRatio:false}} />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default RetentionReport;
